import React, { FC, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineEye, AiFillEyeInvisible } from 'react-icons/ai';
import { LiaRobotSolid } from 'react-icons/lia';
import { useGoogleLogin, CredentialResponse } from '@react-oauth/google';
import { toast } from 'react-toastify';
import { Context } from '../../index';
import { observer } from 'mobx-react-lite';
import CaptchaComponent from './CaptchaComponent';
import gmail from '../../images/gmail2.svg'
const LoginForm: FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loginAttempts, setLoginAttempts] = useState(0);
    const [isCaptchaVisible, setIsCaptchaVisible] = useState(false);
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [tooManyRequests, setTooManyRequests] = useState(false);

    const navigate = useNavigate();
    const isMounted = useRef(true);
    const { store } = useContext(Context);

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const handleLogin = async () => {
        try {
            setLoginAttempts(prev => {
                const newAttempts = prev + 1;
                if (newAttempts >= 3 && !captchaVerified) {
                    setIsCaptchaVisible(true);
                }
                return newAttempts;
            });

            await store.login(email, password);

            if (isMounted.current) {
                setCaptchaVerified(false);
                setTooManyRequests(false);
            }
        } catch (error: any) {
            if (isMounted.current) {
                if (error.response?.status === 429) {
                    setTooManyRequests(true);
                } else {
                    toast.error(error.response?.data?.message || 'Login failed');
                }
            }
        }
    };


// LoginForm.tsx
    const googleLogin = useGoogleLogin({
        onSuccess: async (codeResponse) => { // Renamed to codeResponse for clarity
            const authCode = codeResponse.code; // Use .code
            if (!authCode) {
                toast.error('Google login failed: No authorization code received');
                return;
            }


            try {
                // Ensure your store.googleLogin and backend are expecting an authCode
                const result = await store.googleLogin(authCode);
                if (result.profileComplete) {
                    navigate('/login'); // Or perhaps navigate to dashboard if already logged in
                } else {
                    navigate('/moreinfoplz');
                }
            } catch (error) {
                toast.error('Google login failed on server');
            }
        },
        onError: (error) => {
            toast.error('Google login failed');
        },
        flow: 'auth-code',
    });


    const handleCaptchaVerification = (isHuman: boolean) => {
        if (isHuman) {
            setCaptchaVerified(true);
            setIsCaptchaVisible(false);
        } else {
            toast.error('CAPTCHA verification failed. Please try again.');
        }
    };

    useEffect(() => {
        if (captchaVerified) {
            setLoginAttempts(0);
            setIsCaptchaVisible(false);
            setCaptchaVerified(false);
        }
    }, [captchaVerified]);

    return (
        <div className="jcc center mt-64 mb-32" style={{ maxWidth: 400 }}>
            {tooManyRequests ? (
                <div className="error-message">
                    <LiaRobotSolid style={{ height: '50px', width: '50px' }} />
                    <p>
                        Du är tillfälligt spärrat från att logga in. Vänligen försök igen om 15 minuter eller klicka på
                        länken nedan för att återställa ditt lösenord.
                    </p>
                    <button onClick={() => navigate('/recover')} className="ghostbutton jcc center mt-32 mb-32">
                        Återställ lösenord
                    </button>
                </div>
            ) : (
                <>
                    <h2 className="pb-16">Elevinloggningen</h2>
                    <input
                        onChange={(e) => setEmail(e.target.value)}
                        value={email}
                        minLength={5}
                        type="email"
                        placeholder="E-mail"
                    />
                    <div className="password-input-container">
                        <input
                            onChange={(e) => setPassword(e.target.value)}
                            minLength={8}
                            value={password}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Lösenord"
                        />
                        <span onClick={togglePasswordVisibility} className="eye mb-16">
                            {showPassword ? (
                                <AiFillEyeInvisible style={{ color: '#54319f', width: '30px', height: '30px' }} />
                            ) : (
                                <AiOutlineEye style={{ color: '#54319f', width: '30px', height: '30px' }} />
                            )}
                        </span>
                    </div>

                    {isCaptchaVisible && !captchaVerified && (
                        <CaptchaComponent onVerify={handleCaptchaVerification} />
                    )}
                    {!isCaptchaVisible || captchaVerified ? (
                        <button onClick={handleLogin} className="button center">
                            Login
                        </button>
                    ) : null}

                    <button onClick={() => googleLogin()} className="google-login-button">
                        <img
                            src={gmail}
                            alt="Gmail Logo"
                            style={{
                                verticalAlign: 'middle',
                                marginRight: '8px',
                                position: 'relative',
                                top: '-3px',
                            }}
                        />
                        <span>Sign in with Google</span>
                    </button>

                    <button onClick={() => navigate('/register')} className="ghostbuttonStart">
                        Registrera ett konto
                    </button>
                    <br />
                    <button
                        onClick={() => navigate('/instructorLogIn')}
                        className="ghostbuttonStart mt-32"
                        style={{ maxWidth: 350 }}
                    >
                        Logga in som handledare
                    </button>
                    <br />
                    <p>
                        Glömt ditt lösenord? Återställ
                        <a onClick={() => navigate('/recover')}> här</a>
                    </p>
                </>
            )}
        </div>
    );
};

export default observer(LoginForm);
